import { switchMap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationService } from '../../../core/service/navigation.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { of, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { LocalDate } from '../../../core/model/local-date';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MessageService } from '../../../core/service/message.service';
import { LeaseAgreementService } from '../../service/data/lease-agreement.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import {
  ESignatory,
  InformationChannelKind,
  SignatureReminderSettingKind,
  SignatureTypeKind,
  SigningRoleKind,
} from '../../model/esignatory';
import { RentalObjectCategory } from '../../model/const/enums';
import { RentalObjectKey } from '../../model/rental-object-key';
import { configureESignatoryContactValidation } from '../../helper/esignatory-form-validation';

@Component({
    selector: 'app-esign-key-reciept-document',
    templateUrl: './esign-key-reciept-document.component.html',
    styleUrls: ['./esign-key-reciept-document.component.scss'],
    providers: [NavigationService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ESignKeyRecieptDocumentComponent implements OnDestroy, OnInit {
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _fc: FormControlExFactory,
    private _navigation: NavigationService,
    private _nativeWindow: NativeWindowService,
    private _leaseAgreementService: LeaseAgreementService,
    private _fb: FormBuilder,
    private _messageService: MessageService
  ) {
    this._subscriptions = new Subscription();

    this.form = this._fb.group({
      expirationDate: this._fc.createMandatory(
        'ESIGNATURE.EXPIRATION_DATE',
        LocalDate.fromDate(LocalDate.today().addDays(7)),
        'ESIGNATURE.HELP_TEXT.EXPIRATION_DATE'
      ),
      errandDescription: this._fc.createMandatory('ESIGNATURE.DESCRIPTION', '', 'ESIGNATURE.HELP_TEXT.DESCRIPTION'),
      informWhenSigned: this._fc.create('ESIGNATURE.INFORM_WHEN_SIGNED', 1, null, null, 'ESIGNATURE.HELP_TEXT.INFORM_WHEN_SIGNED'),
      informEmailAddress: this._fc.create('ESIGNATURE.INFORM_EMAIL_ADRESSES', '', null, null, 'ESIGNATURE.HELP_TEXT.INFORM_EMAIL_ADRESSES'),
      signatories: this._fb.array([]),
      attachments: this._fb.array([]),
      keys: this._fb.array([]),
      updateRentalObjectKeys: this._fc.create('KEYTYPE.UPDATE_OBJECT_KEYS', true),
      handOverDate: this._fc.createMandatory('RENTALOBJECT_KEY.KEYS_HANDOUT_DATE', LocalDate.today()),
    });
  }

  form: FormGroup;
  leaseAgreementId: string;
  leaseAgreementNumber: string;
  category: RentalObjectCategory;
  formsForEsign = [];
  mimeTypes: string[] = DocumentTypeMimeTypes.PDF();

  civicNumberMask = /^(\d{8}\-\d{0,4})|(\d{1,8})/;
  readonly civicNumberMaskFormat: string = 'ÅÅÅÅMMDD-NNNN';

  signatureTypeSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.BANKID', SignatureTypeKind.BankId),
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.CLICK', SignatureTypeKind.Click),
  ];

  informationChannels = [
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL', InformationChannelKind.Email),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.SMS', InformationChannelKind.Sms),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL_AND_SMS', InformationChannelKind.EmailAndSms),
  ];

  signingRoles = [
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.NOT_SPECIFIED', SigningRoleKind.NotSpecified),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.COMPANY_REPRESENTATIVE', SigningRoleKind.CompanyRepresentative),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.TENANT', SigningRoleKind.Tenant),
    new SelectionItem('LEASE_AGREEMENT.CREDITOR', SigningRoleKind.Guarantor),
  ];

  signatureReminderSettingSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.NO_REMINDERS', SignatureReminderSettingKind.NoReminders),
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.ONCE_A_DAY', SignatureReminderSettingKind.OnceADay),
  ];

  private addSignatory(signatory: ESignatory) {
    const signatories = this.form.controls['signatories'] as FormArray;
    const signatoryForm = this._fb.group({
        id: this._fc.createHidden(signatory.id),
        civicNumber: this._fc.create('ESIGNATURE.SIGNATORY.CIVIC_NUMBER', signatory.civicNumber),
        email: this._fc.createEmail('ESIGNATURE.SIGNATORY.EMAIL', signatory.email),
        signatureType: this._fc.createMandatory('ESIGNATURE.SIGNATORY.SIGNATURE_TYPE', signatory.signatureType),
        informationChannel: this._fc.createMandatory('ESIGNATURE.SIGNATORY.INFORMATION_CHANNEL', signatory.informationChannel),
        name: this._fc.createMandatory('ESIGNATURE.SIGNATORY.NAME', signatory.name),
        phone: this._fc.create('ESIGNATURE.SIGNATORY.PHONE', signatory.phone),
        signOrder: this._fc.create('ESIGNATURE.SIGNATORY.SIGN_ORDER', signatory.signOrder),
        signingRole: this._fc.createMandatory('COMMON.ROLE', signatory.signingRole),
        signatureReminderSetting: this._fc.createMandatory(
          'ESIGNATURE.SIGNATORY.SIGNATURE_REMINDER_SETTING',
          signatory.signatureReminderSetting
        ),
      });
    signatories.push(signatoryForm);
    this.AddSubscription(configureESignatoryContactValidation(signatoryForm));
  }

  ngOnInit() {
    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('leaseAgreementId');
            if (id) {
              this.leaseAgreementId = id;
              return this._leaseAgreementService.getDataForKeySign(id);
            }
            return of([]);
          })
        )
        .subscribe(data => {
          this._setEsignData(data);
        })
    );
  }

  keys: RentalObjectKey[];

  private _setEsignData(context: any) {
    if (context) {
      this.leaseAgreementNumber = context.leaseAgreementNumber;
      this.category = context.category;
      this.keys = context.keys;
      this.form.controls['errandDescription'].setValue(context.errandDescription);
      this.form.controls['informEmailAddress'].setValue(context.informEmailAddress);

      for (const signatory of context.signatories) {
        this.addSignatory(signatory);
      }

      this.form.markAllAsTouched();
    }
  }

  get signatoryForms(): FormGroup[] {
    return (this.form.controls['signatories'] as FormArray).controls.map(c => c as FormGroup);
  }

  newSignatory() {
    this.addSignatory(new ESignatory());
  }

  deleteSignatory(index: number) {
    const signatories = this.form.controls['signatories'] as FormArray;
    signatories.removeAt(index);
  }

  private readonly _subscriptions: Subscription;
  private _resultSub: Subscription;

  protected AddSubscription(subscription: Subscription): void {
    if (!subscription) {
      throw new Error('Null reference. Object not set.');
    }

    this._subscriptions.add(subscription);
  }

  private clearSubscriptions() {
    if (this._resultSub && !this._resultSub.closed) this._resultSub.unsubscribe();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this.clearSubscriptions();
  }

  cancel() {
    this._navigation.navigateBack();
  }

  previewForm() {
    if (this.form.invalid || this.form.disabled) return;

    let context = this.form.value;
    context.keys = (this.form.controls['keys'] as FormArray).controls.map(c => c.value);
    context.leaseAgreementId = this.leaseAgreementId;

    this.AddSubscription(
      this._leaseAgreementService.getKeyReceiptForm(context).subscribe(res => {
        this._nativeWindow.openFile(res, `${this.leaseAgreementNumber}.pdf`);
      })
    );
  }

  save() {
    if (this.form.disabled) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    let context = this.form.value;
    context.keys = (this.form.controls['keys'] as FormArray).controls.map(c => c.value);
    context.leaseAgreementId = this.leaseAgreementId;

    context.attachments = (this.form.controls['attachments'] as FormArray).controls.map(f => f.value.data);
    this.form.disable();

    this.AddSubscription(
      this._leaseAgreementService.sendKeyReceiptForEsign(context).subscribe(
        () => this._navigation.navigateBack(),
        error => {
          this.form.enable();
        }
      )
    );
  }
}
